/**
 * Represents a named route in an application with an associated path.
 */
export type NamedRoute<T> = {
    path: string;
    parameters: T;
};

/**
 * An object representing route segment names used within the application.
 * Each key in the object corresponds to a specific route, and its value
 * represents the segment for that route.
 */
export const RouteSegments = {
    LOGIN: 'login',
    HOME: 'home',
    LANDING: 'landing',
    APP: 'app',
    WELCOME: 'welcome',
    PLANNER_TASKS: 'planner-tasks',
    STOCKTAKE: 'stocktake',
    TIME_AND_ATTENDANCE: 'time-and-attendance',
    NOTIFICATIONS: 'notifications',
    BIOMETRICS: 'biometrics',
    TASK: 'task',
    DVI: 'dvi',
    INSPECTION_CREATE: 'dvi/inspection/create',
    INSPECTION_DETAILS: 'dvi/inspection/details',
    INSPECTION_LIST: 'dvi/inspection/list',
    CHECK_IN: 'check-in',
    CHECK_IN_FLOW: `check-in/flow`,
    CONTACT_TYPE: 'contact-type',
    CONTACT_TYPE_DETAILS: 'contact-type/details',
    PICKUP_OPTION: 'pickup-option',
    PICKUP_OPTION_DETAILS: 'pickup-option/details',
    CUSTOMER: 'customer',
    CUSTOMER_DETAILS: 'customer/details',
    MESSAGE_CENTER: 'customer/message-center',
    VEHICLE_DETAILS: 'vehicle/details',
    INSPECTION_TEMPLATE_LIST: 'dvi/inspection-template',
    INSPECTION_TEMPLATE_DETAILS: 'dvi/inspection-template/details',
    SKIP_REASONS: 'dvi/skip-reasons',
    APPOINTMENT_ADD_ON_LIST: 'settings/appointment-add-on',
    APPOINTMENT_ADD_ON_FORM: 'settings/appointment-add-on/form',
    APPOINTMENTS: 'appointments',
    APPOINTMENTS_LIST: 'appointments/list',
    APPOINTMENTS_DETAILS: 'appointments/details',
    APPOINTMENTS_SELF_CHECK_IN: 'appointments/self-check-in',
    SETTINGS: 'settings',
    SYSTEM_SETTINGS: 'system-settings',
    CUSTOMER_REPORT_MESSAGES: 'customer-report-messages',
    TENANT: 'tenant',
    TENANT_DETAILS: 'tenant/details',
    MY_TENANT: 'my-tenant',
    USER: 'user',
    USER_DETAILS: 'user/details',
    AGREEMENT: 'agreement',
    AGREEMENT_DETAILS: 'agreement/details',
    SYSTEM_ANNOUNCEMENT: 'system-announcement',
    BRANCH: 'branch',
    FEATURE: 'feature',
    DVI_CONSOLIDATED_REPORT: 'dvi-consolidated-report',
    CODE_LOGIN: 'code-login',
    LAUNCH: 'launch',
    INVENTORY: 'inventory',
    INVENTORY_COUNTING: 'inventory-counting',
    INVENTORY_LIST: 'inventory-list',
    MANUFACTURER: 'manufacturer',
    ADDON: 'addon',
    JOB_CATEGORY: 'job-category',
    USER_DEVICE: 'user-device',
    DEVICE_ALLOWLIST: 'device-allowlist',
    LIST: 'list',
    TIME_CLOCK: 'time-clock',
    DISPATCH: 'dispatch',
    DETAILS: 'details',
    WORK_ORDER: 'work-order',
    RECEIVING_DOCUMENT_PURCHASE_ORDER: 'receiving-document-purchase-order',
    DASHBOARD: 'dashboard',
    THEME_MANAGEMENT: 'theme-management',
    TRANSLATION_MANAGEMENT: 'translation-management',
    FORGOT_PASSWORD: 'forgot-password',
    RESET_PASSWORD: 'reset-password',
    CHANGE_PASSWORD: 'change-password',
    AUDIT_LOG: 'audit-log',
    ROLE_MANAGEMENT: 'role-management',
    TECH_WORK: 'tech-work',
    TECH_WORK_NOTIFICATIONS: 'tech-work/notifications',
    IMPORT: 'import',
    SELF_CHECK_IN: 'self-check-in',
    SELF_CHECK_IN_SETTINGS: 'self-check-in/settings',
    QUOTE: 'quote',
};

/**
 * Represents the route parameters for a planner task.
 */
export type PlannerTaskRouteParams = {
    resetDate?: boolean;
    newBranchCode?: string;
};

/**
 * Represents the parameters required for the Task Detail route.
 */
export type TaskDetailRouteParams = {
    orderNumber?: number;
    branchCode?: string;
    bookingStatus?: string;
    expandComments?: boolean;
    expandAttachments?: boolean;
    salesPersonName?: string;
};

/**
 * Represents the route parameters for user device.
 */
export type UserDeviceRouteParams = {
    'user-id'?: string;
    q?: string;
};

/**
 * The NamedRoutes class contains a collection of static readonly properties
 * that define named routes and their associated paths for an application.
 * These routes are used to navigate to specific sections of the application.
 */
export const NamedRoutes = {
    root(): NamedRoute<object> {
        return {
            path: '/',
            parameters: {},
        };
    },

    login(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.LOGIN}`,
            parameters: {},
        };
    },

    /**
     * The customer link verification page. Carries the access code id when it is known, so the
     * page can ask for that link's credential again instead of presenting a dead form.
     */
    codeLogin(accessCodeId?: string): NamedRoute<object> {
        return {
            path: `/${RouteSegments.CODE_LOGIN}`,
            parameters: accessCodeId ? { id: accessCodeId } : {},
        };
    },

    /**
     * The landing page for an external launch. The launching service redirects here itself with
     * its own parameters, so the app never navigates to it with any.
     */
    launch(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.LAUNCH}`,
            parameters: {},
        };
    },

    home(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.HOME}`,
            parameters: {},
        };
    },

    landing(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.LANDING}`,
            parameters: {},
        };
    },

    welcome(redirectRoute?: string, branchId?: string): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.WELCOME}`,
            parameters: { redirectRoute, branchId },
        };
    },

    userHome(data: PlannerTaskRouteParams): NamedRoute<PlannerTaskRouteParams> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.PLANNER_TASKS}`,
            parameters: data,
        };
    },

    dashboard(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.DASHBOARD}`,
            parameters: {},
        };
    },

    inspectionList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INSPECTION_LIST}`,
            parameters: {},
        };
    },

    inspectionListFiltered(data: { quoteId?: string; status?: string }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INSPECTION_LIST}`,
            parameters: data,
        };
    },

    inspectionAdd(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INSPECTION_CREATE}`,
            parameters: {},
        };
    },

    inspectionDetail(data: { tenantId: string; branchId: string; id: string }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INSPECTION_DETAILS}`,
            parameters: data,
        };
    },

    inspectionTemplateList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.DVI}`,
            parameters: {},
        };
    },

    inspectionTemplateDetail(data: { tenantId: string; branchId: string; id: string | null }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INSPECTION_TEMPLATE_DETAILS}`,
            parameters: data,
        };
    },

    skipReasonList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SKIP_REASONS}`,
            parameters: {},
        };
    },

    skipReasonDetail(data: { id: string | null; scope?: string }): NamedRoute<{ id: string | null; scope?: string }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SKIP_REASONS}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    appointmentAddOnList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.APPOINTMENT_ADD_ON_LIST}`,
            parameters: {},
        };
    },

    completedAppointmentList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.APPOINTMENTS_LIST}`,
            parameters: {},
        };
    },

    selfCheckInAdminList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.APPOINTMENTS_SELF_CHECK_IN}`,
            parameters: {},
        };
    },

    completedAppointmentDetail(data: { tenantId: string; branchId: string; id: string }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.APPOINTMENTS_DETAILS}`,
            parameters: data,
        };
    },

    appointmentAddOnForm(data: { id: string | null }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.APPOINTMENT_ADD_ON_FORM}`,
            parameters: data,
        };
    },

    plannerTasks(data: PlannerTaskRouteParams): NamedRoute<PlannerTaskRouteParams> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.PLANNER_TASKS}`,
            parameters: data,
        };
    },

    stocktake(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.STOCKTAKE}`,
            parameters: {},
        };
    },

    tenantBranchDetail(data: { tenantId: string; id: string | null }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.TENANT}/${RouteSegments.BRANCH}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    tenantFeatureDetail(data: { tenantId: string; id: string | null }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.TENANT}/${RouteSegments.FEATURE}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    branchDetail(data: { tenantId: string; id: string | null }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.BRANCH}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    branchList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.BRANCH}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },

    deviceAllowlistList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.DEVICE_ALLOWLIST}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },

    tenantDetail(data: { id: string | null }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.TENANT_DETAILS}`,
            parameters: data,
        };
    },

    tenantList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.TENANT}`,
            parameters: {},
        };
    },

    myTenant(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.MY_TENANT}`,
            parameters: {},
        };
    },

    userList(data?: { tenantId?: string }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.USER}`,
            parameters: data ?? {},
        };
    },

    userDetail(data: { tenantId: string; id: string | null }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.USER_DETAILS}`,
            parameters: data,
        };
    },

    agreementList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.AGREEMENT}`,
            parameters: {},
        };
    },

    systemAnnouncementList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.SYSTEM_ANNOUNCEMENT}`,
            parameters: {},
        };
    },

    systemAnnouncementDetail(data: { id: string | null }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.SYSTEM_ANNOUNCEMENT}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    agreementDetail(data: { id: string | null; tenantId: string }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.AGREEMENT_DETAILS}`,
            parameters: data,
        };
    },

    timeAndAttendance(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.TIME_AND_ATTENDANCE}`,
            parameters: {},
        };
    },

    notifications(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.NOTIFICATIONS}`,
            parameters: {},
        };
    },

    biometrics(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.BIOMETRICS}`,
            parameters: {},
        };
    },

    taskDetail(data: TaskDetailRouteParams): NamedRoute<TaskDetailRouteParams> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.TASK}`,
            parameters: data,
        };
    },

    contactTypeList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.CONTACT_TYPE}`,
            parameters: {},
        };
    },

    contactTypeDetail(data: { tenantId: string; id: string | null }): NamedRoute<{
        tenantId: string;
        id: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.CONTACT_TYPE_DETAILS}`,
            parameters: data,
        };
    },

    addonList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.ADDON}`,
            parameters: {},
        };
    },

    addonDetail(data: { tenantId: string; id: string | null }): NamedRoute<{
        tenantId: string;
        id: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.ADDON}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    addonNew(data: { tenantId: string }): NamedRoute<{ tenantId: string; new: string }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.ADDON}/${RouteSegments.DETAILS}`,
            parameters: { tenantId: data.tenantId, new: 'true' },
        };
    },

    pickupOptionList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.PICKUP_OPTION}`,
            parameters: {},
        };
    },

    pickupOptionDetail(data: { tenantId: string; value: string | null }): NamedRoute<{
        tenantId: string;
        value: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.PICKUP_OPTION_DETAILS}`,
            parameters: data,
        };
    },

    customerList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.CUSTOMER}`,
            parameters: {},
        };
    },

    customerDetail(
        data: { tenantId: string; id: string | null; isAddingVehicle?: boolean; vehicleId?: string | null },
        dvi = true,
    ): NamedRoute<{
        tenantId: string;
        id: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${dvi ? RouteSegments.DVI + '/' : ''}${RouteSegments.CUSTOMER_DETAILS}`,
            parameters: data,
        };
    },

    messageCenter(customerId?: string, phone?: string): NamedRoute<{ customerId?: string; phone?: string }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.MESSAGE_CENTER}`,
            parameters: { customerId, phone },
        };
    },

    vehicleDetail(
        data: {
            tenantId: string;
            id: string | null;
            customerId: string | null;
            state?: string;
        },
        dvi = true,
    ): NamedRoute<{
        tenantId: string;
        id: string | null;
        customerId: string | null;
        state?: string;
    }> {
        return {
            path: `/${RouteSegments.APP}/${dvi ? RouteSegments.DVI + '/' : ''}${RouteSegments.VEHICLE_DETAILS}`,
            parameters: data,
        };
    },

    dviConsolidatedReport(
        data: { tenantId: string; branchId: string; quoteId: string } | { tenantId: string; branchId: string; workOrderId: string },
    ): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.DVI_CONSOLIDATED_REPORT}/consolidated`,
            parameters: data,
        };
    },

    dviInspectionDownload(data: { tenantId: string; branchId: string; inspectionId: string }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.DVI_CONSOLIDATED_REPORT}/inspection`,
            parameters: data,
        };
    },

    userDevice(data: UserDeviceRouteParams): NamedRoute<UserDeviceRouteParams> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.USER_DEVICE}/${RouteSegments.LIST}`,
            parameters: data,
        };
    },

    inventoryCounting(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INVENTORY_COUNTING}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },

    inventoryCountingDetail(data: { tenantId: string; branchId: string; id: string | null; type: string | null }): NamedRoute<{
        tenantId: string;
        branchId: string;
        id: string | null;
        type: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INVENTORY_COUNTING}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    checkInSearch(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.CHECK_IN}`,
            parameters: {},
        };
    },

    checkInFlow(appointmentId: string): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.CHECK_IN_FLOW}/${appointmentId}`,
            parameters: {},
        };
    },

    timeClockList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.TIME_CLOCK}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },

    dispatchList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.DISPATCH}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },

    dispatchDetail(data: { tenantId: string; branchId: string; id: string | null }): NamedRoute<{
        tenantId: string;
        branchId: string;
        id: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.DISPATCH}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },
    workOrderList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.WORK_ORDER}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },
    rdPoList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INVENTORY}/${RouteSegments.RECEIVING_DOCUMENT_PURCHASE_ORDER}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },
    inventoryCountingLists(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INVENTORY}/${RouteSegments.INVENTORY_LIST}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },
    inventoryCountingListsDetail(data: { tenantId: string; branchId: string; id: string | null }): NamedRoute<{
        tenantId: string;
        branchId: string;
        id: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INVENTORY}/${RouteSegments.INVENTORY_LIST}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },
    inventoryList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.INVENTORY}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },
    manufacturerList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.MANUFACTURER}`,
            parameters: {},
        };
    },

    manufacturerDetail(data: { tenantId: string; id: string | null }): NamedRoute<{
        tenantId: string;
        id: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.MANUFACTURER}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    jobCategoryList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.JOB_CATEGORY}`,
            parameters: {},
        };
    },

    jobCategoryDetail(data: { tenantId: string; id: string | null }): NamedRoute<{
        tenantId: string;
        id: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.JOB_CATEGORY}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    themeManagement(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.THEME_MANAGEMENT}`,
            parameters: {},
        };
    },

    translationManagement(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.TRANSLATION_MANAGEMENT}`,
            parameters: {},
        };
    },

    selfCheckInSettings(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.SELF_CHECK_IN_SETTINGS}`,
            parameters: {},
        };
    },

    systemSettings(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.SYSTEM_SETTINGS}`,
            parameters: {},
        };
    },

    customerReportMessages(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.CUSTOMER_REPORT_MESSAGES}`,
            parameters: {},
        };
    },

    forgotPassword(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.FORGOT_PASSWORD}`,
            parameters: {},
        };
    },

    resetPassword(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.RESET_PASSWORD}`,
            parameters: {},
        };
    },

    changePassword(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.CHANGE_PASSWORD}`,
            parameters: {},
        };
    },

    auditLogList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.AUDIT_LOG}`,
            parameters: {},
        };
    },

    roleManagement(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.ROLE_MANAGEMENT}`,
            parameters: {},
        };
    },

    techWork(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.TECH_WORK}`,
            parameters: {},
        };
    },

    techWorkNotifications(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.TECH_WORK_NOTIFICATIONS}`,
            parameters: {},
        };
    },

    techWorkDetail(data: { id: string }): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.TECH_WORK}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },

    import(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.SETTINGS}/${RouteSegments.IMPORT}`,
            parameters: {},
        };
    },

    quoteList(): NamedRoute<object> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.QUOTE}/${RouteSegments.LIST}`,
            parameters: {},
        };
    },

    quoteDetail(data: { tenantId: string; branchId: string; id: string | null }): NamedRoute<{
        tenantId: string;
        branchId: string;
        id: string | null;
    }> {
        return {
            path: `/${RouteSegments.APP}/${RouteSegments.QUOTE}/${RouteSegments.DETAILS}`,
            parameters: data,
        };
    },
};
